import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Tooltip } from '@ledgerloom/ui';
import { Icon } from './components/ui';
import { can, hasFeature } from './session';
import type { Me } from './api';

// The sidebar's destinations (docs/design/contacts.md). An item with sub-items
// is a group: a button that opens them in place, never a link to a page of its
// own. A destination stays current on every page under it.

export type Badge = 'approvals';
export interface Dest { to: string; label: string; icon: string; match: string[]; exact?: boolean; badge?: Badge; feature?: string; scope?: string }
// A destination shows when the plan includes its feature and the person's role may read it.
const shows = (me: Me, d: Dest) => hasFeature(me, d.feature) && can(me, d.scope);
export interface Group { id: string; label: string; icon: string; children: Dest[] }
export type NavItem = Dest | Group;
export const isGroup = (n: NavItem): n is Group => 'children' in n;

const dest = (to: string, label: string, icon: string, more: Partial<Dest> = {}): Dest => ({ to, label, icon, match: [to], ...more });

export const NAV: NavItem[] = [
  dest('/', 'Home', 'home', { exact: true }),
  dest('/desk', 'Agent Desk', 'team', { badge: 'approvals', match: ['/desk', '/agents', '/team', '/approvals'] }),
  dest('/calendar', 'Calendar', 'calendar', { feature: 'calendar', scope: 'read:calendar' }),
  dest('/mail', 'Mail', 'mail', { feature: 'mail', scope: 'read:mail' }),
  dest('/library', 'Library', 'note', { scope: 'read:assets' }),
  { id: 'work', label: 'Work', icon: 'pipeline', children: [
    dest('/tickets', 'Tickets', 'ticket', { feature: 'tickets', scope: 'read:tickets' }),
    dest('/leads', 'Leads', 'lead', { feature: 'sales', scope: 'read:leads' }),
    dest('/pipeline', 'Pipeline', 'pipeline', { feature: 'sales', scope: 'read:deals' }),
    dest('/deals', 'Deals', 'box', { feature: 'sales', scope: 'read:deals' }),
    dest('/quotes', 'Quotes', 'quote', { feature: 'sales', scope: 'read:quotes', match: ['/quotes', '/products'] }),
    dest('/marketing', 'Marketing', 'megaphone', { feature: 'marketing', scope: 'read:campaigns' }),
    dest('/dashboards', 'Dashboards', 'chart', { feature: 'dashboards', scope: 'read:dashboards' }),
    dest('/ledger', 'Point in time', 'chart'),
  ] },
  { id: 'books', label: 'Books', icon: 'book', children: [
    dest('/books/accounts', 'Chart of accounts', 'list', { feature: 'books', scope: 'read:books' }),
    dest('/books/journal', 'Journal', 'note', { feature: 'books', scope: 'read:books' }),
    dest('/books/trial-balance', 'Trial balance', 'chart', { feature: 'books', scope: 'read:books' }),
    dest('/books/statements', 'Statements', 'chart', { feature: 'books', scope: 'read:books' }),
    dest('/books/parallel-run', 'Parallel run', 'chart', { feature: 'books', scope: 'read:books' }),
  ] },
  { id: 'records', label: 'Records', icon: 'records', children: [
    dest('/contacts', 'Contacts', 'contacts', { scope: 'read:parties' }),
    dest('/activity', 'Activity', 'activity', { scope: 'read:proposals' }),
    dest('/import', 'Import', 'upload', { feature: 'import', scope: 'propose:import' }),
  ] },
  dest('/admin', 'Settings', 'settings'),
];

export const isCurrent = (path: string, n: { match: string[]; exact?: boolean }) =>
  n.exact ? path === '/' : n.match.some((m) => path === m || path.startsWith(`${m}/`));

// Every destination a person may reach, groups flattened, for the phone's More page and redirects.
export const visibleDests = (me: Me): Dest[] => NAV.flatMap((n) => (isGroup(n) ? n.children : [n])).filter((d) => shows(me, d));
export const groupFirst = (me: Me, id: string): string => {
  const g = NAV.find((n) => isGroup(n) && n.id === id) as Group | undefined;
  return g?.children.find((d) => shows(me, d))?.to ?? '/';
};

// Which groups are open is this browser's; storage that throws starts them closed.
const OPEN_KEY = 'll.nav.open';
function useOpenGroups(): [Set<string>, (id: string, open: boolean) => void] {
  const [open, setOpen] = useState<Set<string>>(() => { try { return new Set(JSON.parse(localStorage.getItem(OPEN_KEY) ?? '[]')); } catch { return new Set(); } });
  const set = useCallback((id: string, on: boolean) => setOpen((s) => {
    const next = new Set(s);
    if (on) next.add(id); else next.delete(id);
    try { localStorage.setItem(OPEN_KEY, JSON.stringify([...next])); } catch { /* not remembered */ }
    return next;
  }), []);
  return [open, set];
}

export function SideNav({ me, collapsed, count }: { me: Me; collapsed: boolean; count: (b?: Badge) => ReactNode }) {
  const path = useLocation().pathname;
  const [open, setOpen] = useOpenGroups();
  const link = (d: Dest, sub = false) => {
    const on = isCurrent(path, d);
    const a = (
      <Link key={d.to} to={d.to} className={`${on ? 'active' : ''}${sub ? ' nav-sub' : ''}`.trim() || undefined} aria-current={on ? 'page' : undefined}>
        <Icon name={d.icon} /><span className="nav-label">{d.label}</span>
        {count(d.badge)}
      </Link>
    );
    // Folded to icons, an item names itself on hover or focus; a group's panel already shows its pages' names.
    return collapsed && !sub ? <Tooltip key={d.to} label={d.label} side="right">{a}</Tooltip> : a;
  };
  return (
    <nav className="nav" id="main-nav" aria-label="Main">
      {NAV.map((n) => {
        if (!isGroup(n)) return shows(me, n) ? link(n) : null;
        const kids = n.children.filter((d) => shows(me, d));
        if (!kids.length) return null;
        const here = kids.some((d) => isCurrent(path, d));
        return <NavGroup key={n.id} group={n} kids={kids} here={here} collapsed={collapsed} open={open.has(n.id)} setOpen={(on) => setOpen(n.id, on)} link={link} />;
      })}
    </nav>
  );
}

// Open, a group lists its pages under it; with the sidebar folded to icons, in a panel beside it.
function NavGroup({ group, kids, here, collapsed, open, setOpen, link }: {
  group: Group; kids: Dest[]; here: boolean; collapsed: boolean; open: boolean; setOpen: (on: boolean) => void; link: (d: Dest, sub?: boolean) => ReactNode;
}) {
  const [flyout, setFlyout] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const path = useLocation().pathname;
  const id = `nav-group-${group.id}`;
  useEffect(() => { setFlyout(false); }, [path, collapsed]);
  // Arriving on one of its pages opens the group, before paint; the person can still close it there.
  useLayoutEffect(() => { if (here) setOpen(true); }, [here]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!flyout) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) {
        setFlyout(false);
        if (e instanceof KeyboardEvent) ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [flyout]);
  const expanded = collapsed ? flyout : open;
  return (
    <div className={`nav-group${expanded ? ' open' : ''}${collapsed ? ' flyout' : ''}`} ref={ref}>
      <Folded on={collapsed} label={group.label}>
        <button type="button" className={`nav-group-btn${here ? ' here' : ''}`} aria-expanded={expanded} aria-controls={id}
          onClick={() => (collapsed ? setFlyout((f) => !f) : setOpen(!open))}>
          <Icon name={group.icon} /><span className="nav-label">{group.label}</span>
          {!collapsed && <span className="nav-caret"><Icon name="down" /></span>}
        </button>
      </Folded>
      <div id={id} className="nav-children" hidden={!expanded} role={collapsed ? 'group' : undefined} aria-label={collapsed ? group.label : undefined}>
        {collapsed && <div className="nav-flyout-title" aria-hidden="true">{group.label}</div>}
        {kids.map((d) => link(d, true))}
      </div>
    </div>
  );
}

// A control in the folded sidebar names itself in a tooltip; unfolded, its label is on screen.
export const Folded = ({ on, label, children }: { on: boolean; label: string; children: ReactElement }) => (on ? <Tooltip label={label} side="right">{children}</Tooltip> : children);
